import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { loadState } from "../utils/LocalStorage";
import { PackageOptionsProps, PackageProps } from "../types/data";
import { PackageCategoryType } from "../types/loadedData";

interface TourState {
  activityType: string;
  vehicles: string[];
  editedVehicle: number | null;
  packageData: PackageProps;
  imagesOwnership: boolean;
  gallery: File[] | string[];
  packageCategories: PackageCategoryType[];
  PrepackageDataId: string;
  packageMeetingPointAddress: string | null;
  packageMeetingDifferentAddress: string | null;
  editedPickupIndex: number | null;
  editedPickupAddressIndex: string | null;
  checkAreaOrAddressModal: string;
  feeValue: number | null;
  prePackagesData: any[];
  optionsData: PackageOptionsProps;
  optionsList: any[];
  mealSettingsData: any[];
  packageId: string;
  preOptionsId: string;
}

const initialState: TourState = loadState() || {
  activityType: "",
  vehicles: [],
  editedVehicle: null,
  packageData: {},
  PrepackageDataId: "",
  imagesOwnership: false,
  packageGallery: [],
  packageCategories: [],
  packageMeetingPointAddress: null,
  packageMeetingDifferentAddress: null,
  editedPickupIndex: null,
  editedPickupAddressIndex: null,
  checkAreaOrAddressModal: "PickupArea",
  currentStep: null,
  prePackagesData: [],
  optionsData: {},
  mealSettingsData: [],
  packageId: "",
  optionsList: [],
  preOptionsId: "",
};

// Slice definition remains unchanged
export const tour = createSlice({
  name: "tour",
  initialState,
  reducers: {
    setActivityType: (state, action: PayloadAction<string>) => {
      state.activityType = action.payload;
    },
    setVehicles: (state, action: PayloadAction<string[]>) => {
      state.vehicles = action.payload;
      state.packageData = {
        ...state.packageData,
        modeOfTransport: action.payload,
      };
    },
    setEditedVehicle: (state, action: PayloadAction<number | null>) => {
      state.editedVehicle = action.payload;
    },
    setPackageData: (state, action: PayloadAction<PackageProps>) => {
      state.packageData = { ...state.packageData, ...action.payload };
    },
    setPacakgeMettingPointAddress: (
      state,
      action: PayloadAction<string | null>
    ) => {
      state.packageMeetingPointAddress = action.payload;
    },
    setPacakgeMettingDifferentAddress: (
      state,
      action: PayloadAction<string | null>
    ) => {
      state.packageMeetingDifferentAddress = action.payload;
    },
    setEditedPickupIndex: (state, action: PayloadAction<number | null>) => {
      state.editedPickupIndex = action.payload;
    },
    setEditedPickupAddressIndex: (
      state,
      action: PayloadAction<string | null>
    ) => {
      state.editedPickupAddressIndex = action.payload;
    },
    setCheckAreaOrAddressModal: (state, action: PayloadAction<string>) => {
      state.checkAreaOrAddressModal = action.payload;
    },
    setPrePackageData: (state, action: PayloadAction<string>) => {
      state.PrepackageDataId = action.payload;
    },
    setImagesOwnership: (state, action: PayloadAction<boolean>) => {
      state.imagesOwnership = action.payload;
    },
    setPackageGallery: (state, action: PayloadAction<File[]>) => {
      state.gallery = action.payload;
    },
    setPackageCategories: (
      state,
      action: PayloadAction<PackageCategoryType[]>
    ) => {
      state.packageCategories = action.payload;
    },
    setFeeValue: (state, action: PayloadAction<number>) => {
      state.feeValue = action.payload;
    },
    setPrePackagesData: (state, action: PayloadAction<any[]>) => {
      state.prePackagesData = action.payload;
    },
    setOptionsData: (state, action: PayloadAction<PackageOptionsProps>) => {
      // Clean up keys if pricingOption is fixed
      let newOptionsData = { ...state.optionsData, ...action.payload };
      if (newOptionsData.pricingOption === "fixed") {
        delete newOptionsData.pricingCategories;
        delete newOptionsData.pricePerAgeGroup;
        delete newOptionsData.groupPricing;
        delete newOptionsData.pricing;
      }
      state.optionsData = newOptionsData;
      // Optionally update localStorage if you persist optionsData
      // localStorage.setItem("optionsData", JSON.stringify(state.optionsData));
    },
    setMealSettingsData: (state, action: PayloadAction<any[]>) => {
      state.mealSettingsData = action.payload;
    },
    resetTourState: (state) => {
      localStorage.removeItem("tourState");
      localStorage.removeItem("mealSettingsData");

      state.activityType = "";
      state.vehicles = [];
      state.editedVehicle = null;
      state.packageData = {};
      state.PrepackageDataId = "";
      state.imagesOwnership = false;
      state.gallery = [];
      state.packageCategories = [];
      state.packageMeetingPointAddress = null;
      state.packageMeetingDifferentAddress = null;
      state.editedPickupIndex = null;
      state.editedPickupAddressIndex = null;
      state.checkAreaOrAddressModal = "PickupArea";
      state.mealSettingsData = [];
    },
    resetOptionsData: (state) => {
      localStorage.removeItem("optionsData");
      state.optionsData = { package: null };
      state.preOptionsId = "";
    },
    setPackageId: (state, action: PayloadAction<string>) => {
      state.packageId = action.payload;
    },
    setOptionsList: (state, action: PayloadAction<any[]>) => {
      state.optionsList = action.payload;
      localStorage.setItem("optionsList", JSON.stringify(state.optionsList));
    },
    setPreOptionsId: (state, action: PayloadAction<string>) => {
      state.preOptionsId = action.payload;
    },
  },
});

export const {
  setActivityType,
  setVehicles,
  setEditedVehicle,
  setPackageData,
  setPrePackageData,
  setPackageGallery,
  setPackageCategories,
  setImagesOwnership,
  setPacakgeMettingPointAddress,
  setEditedPickupIndex,
  setEditedPickupAddressIndex,
  setCheckAreaOrAddressModal,
  resetTourState,
  resetOptionsData,
  setFeeValue,
  setPacakgeMettingDifferentAddress,
  setPrePackagesData,
  setOptionsData,
  setMealSettingsData,
  setPackageId,
  setOptionsList,
  setPreOptionsId,
} = tour.actions;

export default tour.reducer;
export { initialState };
